import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import mapKeys from 'lodash/mapKeys';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import kebabCase from 'lodash/kebabCase';
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import attempt from 'lodash/attempt';
import isError from 'lodash/isError';
import isNil from 'lodash/isNil';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a snake_case string type to camelCase by splitting on underscores,
 * lower-casing the first segment and capitalising the rest.
 *
 * Mirrors lodash `camelCase` for simple underscore-separated keys.
 */
type CamelCaseJoin<S extends string> = S extends `${infer Head}_${infer Tail}`
    ? `${Lowercase<Head>}${CamelCaseJoin<Capitalize<Tail>>}`
    : Lowercase<S>;

type CamelCase<S extends string> = CamelCaseJoin<S>;

/**
 * Maps all keys of an object type to their camelCase equivalents. Optionality and
 * readonly modifiers on the source keys are preserved.
 */
export type CamelCaseKeys<T extends object> = {
    [K in keyof T as K extends string ? CamelCase<K> : K]: T[K];
};

/**
 * Returns the same object with the keys camel cased
 * @param {Object} obj input object
 * @returns {Object} object with the keys camel cased
 * */
export function camelCaseObjectKeys<T extends object> (
    obj: T,
): CamelCaseKeys<T> {
  return mapKeys(obj, (v, k) => camelCase(k)) as unknown as CamelCaseKeys<T>;
}

/**
 * Inserts an underscore before every uppercase letter and lowercases it
 */
type SnakeCaseChars<S extends string> = S extends `${infer C}${infer Rest}`
    ? C extends Uppercase<C>
        ? C extends Lowercase<C>
            ? `${C}${SnakeCaseChars<Rest>}`
            : `_${Lowercase<C>}${SnakeCaseChars<Rest>}`
        : `${C}${SnakeCaseChars<Rest>}`
    : S;

type StripLeadingUnderscores<S extends string> = S extends `_${infer Rest}`
    ? StripLeadingUnderscores<Rest>
    : S;

/**
 * Converts a string type from camelCase/PascalCase to snake_case at the type level.
 *
 * Mirrors lodash `snakeCase` for alphabetic keys. It does *not* reproduce lodash's
 * handling of digit boundaries (`line1` -> `line_1`), acronym runs (`navUSD` -> `nav_usd`)
 * or non-alphanumeric separators (`first name` -> `first_name`) — keys like those resolve
 * to a type that differs from the runtime result.
 */
type SnakeCase<S extends string> = StripLeadingUnderscores<SnakeCaseChars<S>>;

/**
 * Maps all keys of an object type to their snake_case equivalents. Optionality and
 * readonly modifiers on the source keys are preserved.
 */
export type SnakeCaseKeys<T extends object> = {
    [K in keyof T as K extends string ? SnakeCase<K> : K]: T[K];
};

/**
 * Returns the same object with the keys snake cased
 * @param {Object} obj input object
 * @returns {Object} object with the keys snake cased
 * */
export function snakeCaseObjectKeys<T extends object> (
    obj: T,
): SnakeCaseKeys<T> {
  return mapKeys(obj, (v, k) => snakeCase(k)) as unknown as SnakeCaseKeys<T>;
}

/**
 * Sluggifies a string
 * sluggify('Hello World'); // 'hello-world'
 * @param {string} str input string
 * @returns {string} sluggified string
 * */
export const sluggify = (str: string): string => {
  return kebabCase(str);
};

/**
 * checks if an email address is valid
 * @param {string} email input
 * @returns {boolean} true if valid, false if not
 * */
export const isEmailValid = (email: string): boolean => {
  const re =
      /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

/**
 * returns an object grouping of items by the given key
 * @param {Array} items input array
 * @param {string | number} key key to group by
 * @returns {Object} object grouping of items by the given key
 * */
export function convertArrayToMap<T, K extends string | number | symbol>(
    items: T[],
    key: K,
): Record<string | number, T> {
  return keyBy(items, key);
}

/**
 * Groups an array of items by the given key. Returns a Record of key value pairs
 * with the key being the key and the value being an array of items
 * @param {Array} items input array
 * @param {string | number} key key to group by
 * @returns {Object} object grouping of items by the given key
 * */
export function groupByKeys<T>(items: T[], key: string | number): Record<string | number, T[]> {
  return groupBy(items, key);
}

/**
 * Updates an object in an array of items. if the object is not in the array, returns the initial array as is
 * if the object is in the array, updates the object and returns the array with the updated object.
 * @param {T} items Items in the array
 * @param {T} item Item to update
 * @param {unknown} key Key to use to find the item, defaults to id.
 * @returns {T[]} Array with the updated item
 */
export function updateObjectInArray<T>(
    items: T[],
    item: T,
    key: string | number | unknown = 'id',
): T[] {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return items.map((i) => (i[key] === item[key] ? item : i));
}

/**
 * Check if string is a valid url
 * @param {String} url URL
 * @returns {Boolean} true if url is a valid url, false otherwise
 * */
export function isValidUrl(url: string): boolean {
  const matchpattern =
      /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)$/gm;
  return matchpattern.test(url);
}

/**
 * Checks if a collection of items T contains T
 * @param {T[]} items Items to iterate over
 * @param {T} item Item to check if it is in the collection
 * @param {unknown} key Key to use to find the item, defaults to id.
 * @returns {boolean} true if the item is in the collection, false otherwise
 */
export function collectionContains<T>(
    items: T[],
    item: T,
    key: string | number | unknown = 'id',
): boolean {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return items.some((i) => i[key] === item[key]);
}

/**
 * Checks if an object is empty
 * @param {Record<T>} obj object to check
 * @returns boolean value indicating if the object is empty
 */
export function isValidObject(obj: unknown): boolean {
  return !isEmpty(obj);
}

export function safelyParseJson<T>(json: string): T | null {
  const result = attempt(JSON.parse, json);
  if (isError(result)) {
    return null;
  }
  return result;
}

/**
 * Strips a title into title and a subtitle
 * @param str String to split
 * @param sep separator, defaults to ' '
 * @returns split up string
 */
export function stripIntoTitleAndSubtitle(title: string, sep = ' '): string[] {
  // check if title has puncuation marks, e.g. full stops
  const fullStopIdx = title.indexOf('.');
  const newSep = fullStopIdx !== -1 ? title[fullStopIdx] : sep;
  const [newTitle, ...subtitle] = title.split(newSep);
  return [newTitle, subtitle.join(sep).trim()];
}

/**
 * Checks if a value is null or undefined or "null"
 * @param {T} value Value to check
 * @returns {boolean} true if value is null or undefined or "null"
 */
export function isInvalidValue<T>(value: T): boolean {
  return isNil(value) || (value as unknown as string) === 'null';
}

export function changedArray(a: Array<unknown> = [], b: Array<unknown> = []): boolean {
  return a.length !== b.length || a.some((item, index) => !Object.is(item, b[index]));
}

/**
 * Chunks an array into smaller arrays of a given size
 * @param array Array to chunk
 * @param chunkSize chunk size
 * @returns Array of arrays with each sub array being of a given chunk size
 */
export function chunkItems<T>(array: T[], chunkSize: number): T[][] {
  return array.reduce((result, item, index) => {
    if (index % chunkSize === 0) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      result.push([]);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    result[result.length - 1].push(item);
    return result;
  }, []);
}
